<template>
  <organism-backup-management
    v-if="filterData"
    :currentVeeamId="currentServerVeeam.id"
    :filterList="filterData"
    :statusOption="statusOption"
    :resultOption="resultOption"
    :fields="fields"
    :items="backupList"
    @onUpdateFilterData="onUpdateFilterData"
    @onUpdateCurrentPage="onUpdateCurrentPage"
    @onSetDefaultFilterData="onSetDefaultFilterData"
    @delete="deleteByApi"
  ></organism-backup-management>
</template>

<script>
import OrganismBackupManagement from "@/components/organisms/backupManagement/OrganismBackupManagement.vue"
import { getBackupList, deleteJob } from "@/api/api-module3"
import { DateTime } from "luxon"
import { mapActions, mapGetters } from "vuex"
import mixinsValidator from "@/mixins/mixinsValidator"
import mixins from "@/mixins"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"

export default {
  name: "BackupManagement",
  components: { OrganismBackupManagement },
  mixins: [mixins, mixinsValidator, mixinsObjectHelper],
  data() {
    return {
      statusOption: ["Running", "Disabled", "Inactive"],
      resultOption: ["Success", "Warning", "Failed"],
      fields: [
        {
          key: "status",
          label: "STATUS",
          thClass: "w-25px",
          tdClass: "w-25px",
        },
        {
          key: "name",
          label: "NAME",
          thClass: "w-25px",
          tdClass: "w-25px",
        },
        {
          key: "lastRun",
          label: "LAST RUN",
          thClass: "w-25px",
          tdClass: "w-25px",
        },
        {
          key: "nextRun",
          label: "NEXT RUN",
          thClass: "w-25px",
          tdClass: "w-25px",
        },
        {
          key: "deleteIcon",
          label: "",
          thClass: "w-25px",
          tdClass: "w-25px",
        },
      ],
      backupList: [],
      filterData: null,
    }
  },
  computed: {
    ...mapGetters("app", ["getCurrentServerVeeam"]),
    ...mapGetters("appFilter", ["getBMMBackupFilter"]),

    currentServerVeeam() {
      return this.getCurrentServerVeeam
    },
  },
  watch: {
    currentPage() {
      this.getDataFromApi()
    },
    currentServerVeeam() {
      if (this.hasString(this.currentServerVeeam.id)) {
        return this.getBackupListFromApi()
      }
    },
  },
  async mounted() {
    this.setShowVeeam(true)
    await this.getDataFromApi()
  },
  beforeDestroy() {
    this.setShowVeeam(false)
  },
  methods: {
    ...mapActions("app", ["setShowVeeam", "setCurrentStatusVeeam"]),
    ...mapActions("appFilter", ["setBMMBackupFilter"]),

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getBMMBackupFilter
      this.filterData = this.deepCopyObject(filterDataFromVuex)
    },

    async getDataFromApi() {
      this.cloneFilterDataFromVuex()
      if (this.hasString(this.currentServerVeeam.id)) {
        await this.getBackupListFromApi()
      }
    },
    async getBackupListFromApi() {
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const filterData = this.filterDecoration()
      await getBackupList(filterData)
        .then((response) => {
          if (response) {
            this.filterData.total = response.data.pagination.total
            const updatedFilter = {
              ...this.getBMMBackupFilter,
              total: response.data.pagination.total,
            }
            this.setBMMBackupFilter(updatedFilter)
            this.backupList = response.data.data.map((backup) => {
              return {
                id: backup.id,
                name: backup.name,
                type: backup.type,
                description: backup.description,
                status: backup.status,
                lastRun: {
                  date: backup.lastRun
                    ? DateTime.fromISO(backup.lastRun).toFormat("dd/MM/yyyy")
                    : null,
                  time: backup.lastRun
                    ? DateTime.fromISO(backup.lastRun).toFormat("HH:mm:ss")
                    : null,
                },
                lastResult: backup.lastResult,
                nextRun: {
                  date: backup.nextRun
                    ? DateTime.fromISO(backup.nextRun).toFormat("dd/MM/yyyy")
                    : null,
                  time: backup.nextRun
                    ? DateTime.fromISO(backup.nextRun).toFormat("HH:mm:ss")
                    : null,
                },
                workload: backup.workload,
                repository: backup.repositoryId,
                repositoryName: backup.repositoryName,
                objectsCount: backup.objectsCount,
                sessionId: backup.sessionId,
              }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    filterDecoration() {
      const filter = {
        veeamId: this.currentServerVeeam.id,
        statusFilter: this.filterData?.status?.toLowerCase() || undefined,
        lastResultFilter: this.toPascalCase(this.filterData.lastResult),
        nameFilter: this.filterData.name
          ? `*${this.filterData.name}*`
          : undefined,
        Page: this.filterData.currentPage,
        PageSize: 10,
      }
      return filter
    },

    // click filter
    onUpdateFilterData(filter) {
      filter.currentPage = 1
      this.setBMMBackupFilter(filter)
      this.getBackupListFromApi()
    },

    onUpdateCurrentPage(filter) {
      const updatedFilter = {
        ...this.getBMMBackupFilter,
        currentPage: filter.currentPage,
      }
      this.setBMMBackupFilter(updatedFilter)
      this.getBackupListFromApi()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        name: "",
        status: "",
        lastResult: "",
        currentPage: 1,
        total: 0,
      }
      this.setBMMBackupFilter(defaultFilter)
      this.getBackupListFromApi()
    },

    toPascalCase(words) {
      return words
        ? words.replace(/\w+/g, function (w) {
            return w[0].toUpperCase() + w.slice(1).toLowerCase()
          })
        : undefined
    },

    async deleteByApi(id) {
      this.showLoading(true)
      deleteJob(id, this.currentServerVeeam.id)
        .then(async (response) => {
          if (response && response.status === "ok") {
            this.toastSuccess("Backup deleted successfully")

            await this.getBackupListFromApi()
          }
        })
        .catch(() => {
          this.toastError("Backup deleted failed")
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style lang="scss" scoped></style>
